import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Style } from '@capacitor/status-bar';

import { SuccessStep } from '@mopla/constants';
import {
	Footer,
	ForgotPasswordForm,
	MainWithFooterTemplate,
	useStatusBarStyleSync,
} from '@mopla/ui';
import moplaWelcomeLogo from '@mopla/ui/icons/moplaWelcomeLogo.svg?url';
import { ContentWrapper } from '@mopla/ui/layouts/WelcomeScreen';

import { WelcomeStep } from '../../constants';
import { useWelcomePageStoreContext } from '../../useWelcomePageStore';
import { TSetInterceptor } from '../../utils/useInterceptor';
import { useSubmitForgotPassword } from '../../utils/useSubmitForgotPassword';
import { MoplaLogoImgStyled } from '../styled';

export interface IForgotPasswordFlowProps {
	setBackInterceptor: TSetInterceptor;
	setCloseInterceptor: TSetInterceptor;
	onBottomSliderClose: VoidFunction;
}

export const ForgotPasswordFlow: FC<IForgotPasswordFlowProps> = ({
	setBackInterceptor,
	setCloseInterceptor,
	onBottomSliderClose,
}) => {
	const {
		dispatch,
		successType,
		isForgotPasswordError,
		loginTitleComp,
		setStep,
	} = useWelcomePageStoreContext();
	const { push, pop } = useStatusBarStyleSync();
	const navigate = useNavigate();
	const { onSubmitForgotPassword } = useSubmitForgotPassword();
	const isForgotPwdEmailSent =
		successType === SuccessStep.forgotPasswordEmailSent;

	const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');

	const closeErrorHandler = useCallback(() => {
		dispatch({ type: 'SET_FORGOT_PWD_ERROR', payload: false });
	}, [dispatch]);

	const handleBottomSliderBackClick = useCallback(() => {
		if (loginTitleComp) {
			setStep(WelcomeStep.verifyEmail);
		} else {
			navigate(-1);
		}

		return false;
	}, [loginTitleComp, setStep, navigate]);

	useEffect(() => {
		if (!isForgotPwdEmailSent) {
			setBackInterceptor(handleBottomSliderBackClick);
		}

		return () => {
			setBackInterceptor(null);
		};
	}, [handleBottomSliderBackClick, isForgotPwdEmailSent, setBackInterceptor]);

	useEffect(() => {
		if (isForgotPwdEmailSent) {
			setCloseInterceptor(onBottomSliderClose);
		}

		return () => {
			setCloseInterceptor(null);
		};
	}, [onBottomSliderClose, isForgotPwdEmailSent, setCloseInterceptor]);

	useEffect(() => {
		push({
			title: 'ForgotPasswordFlow',
			style: Style.Light,
		});

		return () => {
			pop();
		};
	}, [push, pop]);

	return (
		<ForgotPasswordForm
			onSubmit={onSubmitForgotPassword}
			successStep={successType}
			error={isForgotPasswordError}
			closeError={closeErrorHandler}
			setForgotPasswordEmail={setForgotPasswordEmail}
			forgotPasswordEmail={forgotPasswordEmail}
			render={({ content, submit }) => (
				<MainWithFooterTemplate
					className="mainWithFooter"
					content={
						<ContentWrapper>
							{!successType && (
								<MoplaLogoImgStyled
									className="logo"
									src={moplaWelcomeLogo}
									alt=""
								/>
							)}
							{content}
						</ContentWrapper>
					}
					footer={<Footer>{submit}</Footer>}
				/>
			)}
		/>
	);
};
